"use client"

import { useEffect, useRef } from "react"
import { useInView, useMotionValue, useSpring } from "motion/react"
import { cn } from "@/utils/classnames"
import { TextProps } from "@/elements/Text/Text"
import { Text } from "@/elements"

export function NumberTicker({
  to,
  from = 0,
  direction = "up",
  delay = 0,
  decimalPlaces = 0,
  textProps,
  prefix = "",
  suffix = "",
}: {
  to: number
  from: number
  direction?: "up" | "down"

  decimalPlaces?: number
  delay?: number // delay in ms
  duration?: number // duration in ms
  textProps: TextProps
  prefix?: string
  suffix?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const motionValue = useMotionValue(direction === "down" ? to : from)
  const springValue = useSpring(motionValue, {
    damping: 50,
    stiffness: 100,
  })
  const isInView = useInView(ref, { once: true, margin: "0px" })

  useEffect(() => {
    isInView &&
      setTimeout(() => {
        motionValue.set(direction === "down" ? from : to)
      }, delay)
  }, [motionValue, isInView, delay, from, to, direction])

  useEffect(
    () =>
      springValue.on("change", latest => {
        if (ref.current) {
          ref.current.textContent = `
            ${prefix}${Intl.NumberFormat("en-US", {
              minimumFractionDigits: decimalPlaces,
              maximumFractionDigits: decimalPlaces,
            }).format(Number(latest.toFixed(decimalPlaces)))}${suffix}`
        }
      }),
    [springValue, decimalPlaces]
  )

  return (
    <Text
      {...textProps}
      className={cn("text-black dark:text-white inline-block tabular-nums tracking-wider", textProps?.className)}
      ref={ref}>
      {prefix}
      {direction === "up" ? from : to}
      {suffix}
    </Text>
  )
}
